import { DateTime } from 'luxon';
import { AnyAction } from 'redux';
import { ThunkDispatch, ThunkAction } from 'redux-thunk';

import { MessageTemplate, GreetSendType, Greet } from '~/types/models';
import * as actions from './Actions';
import { createReducer } from '@reduxjs/toolkit';

import { Thumbnail } from '@vimily/bonjoro-ui-components-v2/VideoRecorder';

export interface RecorderStateProps {
  thumbs: Thumbnail[];
  thumbIndex: number | null;
  message: string;
  messageTemplate: MessageTemplate | null;
  hasCustomMessage: boolean;
  blob: Blob | null;
  isExternalVideo: boolean;
  sendType: GreetSendType;
  scheduledDateTime: DateTime | null;
  duration: number | null;
  isFirstTime: boolean;
  showThumbnailSelection: boolean;
  greet: Greet | null;
  videoSourceUrl?: string | null;
}

const initialState: RecorderStateProps = {
  thumbs: [],
  thumbIndex: null,
  message: '',
  messageTemplate: null,
  hasCustomMessage: false,
  blob: null,
  isExternalVideo: false,
  sendType: GreetSendType.Default,
  scheduledDateTime: null,
  duration: null,
  isFirstTime: false,
  showThumbnailSelection: false,
  greet: null,
  videoSourceUrl: null,
};

export type RecorderStateFunction = () => RecorderStateProps;

export type ThunkExtraArg = undefined;

type ActionMethods = keyof typeof actions;
type AllActionsObject = typeof actions;
type Actions = Extract<AllActionsObject[ActionMethods], AnyAction> | AnyAction;

export type Dispatch<T extends Actions = AnyAction> = ThunkDispatch<
  RecorderStateProps,
  ThunkExtraArg,
  T
>;

export type ThunkResult<R, A extends Actions> = ThunkAction<
  R,
  RecorderStateProps,
  ThunkExtraArg,
  A
>;

const recorderReducer = createReducer(initialState, (builder) => {
  builder.addCase(actions.init, (state) =>
    recorderReducer(initialState, actions.setGreet(state.greet!))
  );

  builder.addCase(actions.setThumbnail, (state, action) => {
    state.thumbIndex = action.payload;
  });

  builder.addCase(actions.setMessage, (state, action) => {
    state.message = action.payload;
  });

  builder.addCase(actions.setBlob, (state, action) => {
    state.blob = action.payload;
  });

  builder.addCase(actions.setIsExternalVideo, (state, action) => {
    state.isExternalVideo = action.payload;
  });

  builder.addCase(actions.setDuration, (state, action) => {
    state.duration = action.payload;
  });

  builder.addCase(actions.setMessageTemplate, (state, action) => {
    state.messageTemplate = action.payload;
  });

  builder.addCase(actions.setIsFirstTime, (state, action) => {
    state.isFirstTime = action.payload;
  });

  builder.addCase(actions.setGreet, (state, action) => {
    state.greet = action.payload;

    if (!state.messageTemplate && action.payload) {
      state.messageTemplate = action.payload.message_template;
    }
  });

  builder.addCase(actions.setHasCustomMessage, (state, action) => {
    state.hasCustomMessage = action.payload;
  });

  builder.addCase(actions.setSendType, (state, action) => {
    state.sendType = action.payload;
  });

  builder.addCase(actions.setManualScheduleDateTime, (state, action) => {
    state.scheduledDateTime = action.payload;
  });

  builder.addCase(actions.setShowThumbnailSelection, (state, action) => {
    state.showThumbnailSelection = action.payload;
  });

  builder.addCase(actions.setVideoSourceUrl, (state, action) => {
    state.videoSourceUrl = action.payload;
  });

  builder.addCase(actions.setThumbnails, (state, action) => {
    state.thumbs = action.payload;
  });
});

export default recorderReducer;
